import { useMemo, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { Spinner, Upload, X } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import {
  LocationImportFlow,
  emptyLocationImportState,
  locationImportIsReady,
  type LocationImportState,
} from '@/components/LocationImportFlow';
import { transformLocationRows } from '@/lib/locationImport';
import { bulkImportLocations } from '@/api/locations';
import { ApiError } from '@/api/client';

interface Props {
  slug: string;
  onClose: () => void;
}

// LocationImportDialog wraps LocationImportFlow with submit chrome. The
// caller (LocationsPanel) decides when to open / close; this component owns
// the import state for its lifetime, calls bulkImportLocations on submit,
// and invalidates the locations list cache so the panel reflects the new
// rows when the dialog closes.
export function LocationImportDialog({ slug, onClose }: Props) {
  const { user } = useAuth();
  const token = user?.access_token ?? null;
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();

  const [state, setState] = useState<LocationImportState>(emptyLocationImportState);
  const language = useMemo(() => i18n.language || 'en', [i18n.language]);
  // Country region biases Places results toward the active locale. SE for
  // sv-SE, NO for nb-NO etc. Falls back to SE since SportAdmin's user base
  // is Nordic; misroutes don't fail the import, they just rank slightly
  // worse.
  const region = useMemo(() => regionFromLocale(language), [language]);

  const onPatch = (patch: Partial<LocationImportState>) =>
    setState((prev) => ({ ...prev, ...patch }));

  const importMut = useMutation({
    mutationFn: () => {
      const rows = transformLocationRows(state.resolvedRows, state.splitOption);
      return bulkImportLocations(token, slug, rows);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['locations', slug] });
      onClose();
    },
  });

  const ready = locationImportIsReady(state);
  const errorMsg = (() => {
    const err = importMut.error;
    if (!err) return null;
    if (err instanceof ApiError) {
      if (err.code === 'validation_failed') {
        return t('locations.import.error.validation_failed', { count: 1 });
      }
      if (err.code === 'forbidden_by_scope') {
        return t('locations.import.error.forbidden');
      }
    }
    return t('locations.import.error.unknown');
  })();

  return (
    <DialogPrimitive.Root open>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-[60] bg-foreground/30"
          onClick={() => !importMut.isPending && onClose()}
        />
        <DialogPrimitive.Content
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            if (!importMut.isPending) onClose();
          }}
          onOpenAutoFocus={(e) => e.preventDefault()}
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-[61] flex max-h-[90vh] w-[min(92vw,52rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-card text-foreground shadow-xl"
        >
          <header className="flex items-center justify-between border-b border-border px-6 py-4">
            <DialogPrimitive.Title className="font-heading text-lg font-bold">
              {t('locations.import.title')}
            </DialogPrimitive.Title>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('common.cancel')}
              onClick={onClose}
              disabled={importMut.isPending}
            >
              <X weight="bold" />
            </Button>
          </header>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <LocationImportFlow
              token={token}
              language={language}
              region={region}
              state={state}
              onChange={onPatch}
            />
            {errorMsg && (
              <p role="alert" className="error mt-3">
                {errorMsg}
              </p>
            )}
          </div>

          <footer className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={importMut.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => importMut.mutate()}
              disabled={!ready || importMut.isPending}
            >
              {importMut.isPending ? <Spinner weight="bold" className="animate-spin" /> : <Upload weight="bold" />}
              {t('locations.import.submit', { count: state.resolvedRows.length })}
            </Button>
          </footer>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function regionFromLocale(locale: string): string {
  const lower = locale.toLowerCase();
  if (lower.startsWith('sv')) return 'SE';
  if (lower.startsWith('no') || lower.startsWith('nb') || lower.startsWith('nn')) return 'NO';
  if (lower.startsWith('da')) return 'DK';
  if (lower.startsWith('fi')) return 'FI';
  return 'SE';
}

